import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CanadianAddressAutocomplete from '../CanadianAddressAutocomplete.jsx';
import MovdRouteMap from './MovdRouteMap.jsx';
import { ROOM_DEFS } from '../steps/InventoryStep.jsx';
import MovdButton from '../../design-system/MovdButton.jsx';
import MovdInput from '../../design-system/MovdInput.jsx';
import MovdQuantityControl from '../../design-system/MovdQuantityControl.jsx';
import MovdJourneyShell from '../../design-system/MovdJourneyShell.jsx';
import MovdFormSection from '../../design-system/MovdFormSection.jsx';
import MovdFormRow from '../../design-system/MovdFormRow.jsx';
import MovdFormActions from '../../design-system/MovdFormActions.jsx';
import { movdTokens } from '../../design-system/movdTokens.js';

const MOVE_SIZES = [
  { value: 'studio', label: 'Studio / 1 bedroom' },
  { value: '2br', label: '2 bedrooms' },
  { value: '3br', label: '3 bedrooms' },
  { value: '4br+', label: '4+ bedrooms' },
  { value: 'office', label: 'Office / commercial' },
];

const ARRIVAL_WINDOWS = ['8–10 AM', '10 AM–12 PM', '12–2 PM', '2–4 PM', 'Flexible'];

const CATALOG = ROOM_DEFS.flatMap(room => room.items.map(item => ({
  ...item,
  id: item.itemId || item.id || `${room.key}:${item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
  category: room.name,
  room: room.key,
})));

const initialMove = {
  route: { origin: '', destination: '' },
  moveDate: '',
  moveWindow: '',
  moveSize: '',
  quoteMode: null,
  inventory: [],
  access: {
    origin: { floor: '', elevator: false, stairs: false, carry: 'short' },
    destination: { floor: '', elevator: false, stairs: false, carry: 'short' },
  },
  routeMeta: { distanceKm: null, durationMinutes: null },
};

const countItems = inventory => inventory.reduce((total, item) => total + item.qty, 0);

// One shared pill/toggle button style so route windows, move sizes, mode
// cards, and access toggles don't each invent their own button language.
const pillStyle = (active, { block = false } = {}) => ({
  display: block ? 'flex' : 'inline-flex',
  width: block ? '100%' : 'auto',
  justifyContent: block ? 'space-between' : 'center',
  alignItems: 'center',
  gap: 8,
  background: active ? movdTokens.color.teal : 'transparent',
  color: active ? movdTokens.color.ink : movdTokens.color.text,
  border: `1px solid ${active ? movdTokens.color.teal : 'rgba(255,255,255,.28)'}`,
  borderRadius: movdTokens.radius.control,
  padding: '10px 16px',
  fontSize: '.9rem',
  fontWeight: 800,
  fontFamily: 'inherit',
  cursor: 'pointer',
  transition: `background ${movdTokens.motion.micro}, border-color ${movdTokens.motion.micro}`,
});

const linkButtonStyle = {
  background: 'transparent',
  border: 'none',
  color: movdTokens.color.tealSoft,
  fontFamily: 'inherit',
  fontSize: '.85rem',
  fontWeight: 700,
  cursor: 'pointer',
  padding: '8px 0',
};

function MoveProfile({ move, compact = false }) {
  const itemCount = countItems(move.inventory);
  const accessCount = [move.access?.origin, move.access?.destination].filter(place => place?.elevator || place?.stairs || place?.floor || place?.carry !== 'short').length;
  return (
    <div style={{ color: movdTokens.color.text, borderTop: '1px solid rgba(255,255,255,.18)', borderBottom: '1px solid rgba(255,255,255,.18)', padding: compact ? '8px 0' : '20px 0' }}>
      <p style={{ margin: 0, color: movdTokens.color.tealSoft, fontSize: compact ? '.58rem' : '.68rem', fontWeight: 900, letterSpacing: '.16em' }}>YOUR MOVE</p>
      <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr auto 1fr' : '1fr 34px 1fr', gap: compact ? 6 : 10, alignItems: 'center', marginTop: compact ? 4 : 10 }}>
        <strong style={{ fontSize: compact ? '.78rem' : '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{move.route.origin || 'Pickup location'}</strong>
        <span aria-hidden="true" style={{ color: '#38BDF8', fontSize: compact ? 14 : 18, textAlign: 'center' }}>→</span>
        <strong style={{ fontSize: compact ? '.78rem' : '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{move.route.destination || 'Drop-off location'}</strong>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginTop: compact ? 6 : 10 }}>
        {move.moveDate && <span style={{ color: 'rgba(255,255,255,.7)', fontSize: '.78rem' }}>{new Date(`${move.moveDate}T12:00:00`).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}</span>}
        {move.moveSize && <span style={{ color: 'rgba(255,255,255,.7)', fontSize: '.78rem' }}>{move.moveSize}</span>}
        {itemCount > 0 && <span style={{ color: 'rgba(255,255,255,.7)', fontSize: '.78rem' }}>{itemCount} items</span>}
        {accessCount > 0 && <span style={{ color: 'rgba(255,255,255,.7)', fontSize: '.78rem' }}>{accessCount} access details</span>}
      </div>
    </div>
  );
}

export default function MoveWorkspace({ onClose }) {
  const navigate = useNavigate();
  const [move, setMove] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem('movd.move') || '{}');
      return {
        ...initialMove,
        ...saved,
        route: { ...initialMove.route, ...(saved.route || {}) },
        access: {
          origin: { ...initialMove.access.origin, ...(saved.access?.origin || {}) },
          destination: { ...initialMove.access.destination, ...(saved.access?.destination || {}) },
        },
        routeMeta: { ...initialMove.routeMeta, ...(saved.routeMeta || {}) },
        inventory: Array.isArray(saved.inventory) ? saved.inventory : [],
      };
    } catch { return initialMove; }
  });
  const [surface, setSurface] = useState('route');
  const [inventoryCategory, setInventoryCategory] = useState(ROOM_DEFS[0]?.name || 'Living Room');
  const [search, setSearch] = useState('');
  const [customItemName, setCustomItemName] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    sessionStorage.setItem('movd.move', JSON.stringify(move));
  }, [move]);

  const update = useCallback((patch) => setMove(previous => ({ ...previous, ...patch })), []);
  const updateRoute = useCallback((field, value) => setMove(previous => ({ ...previous, route: { ...previous.route, [field]: value } })), []);
  const updateRouteMeta = useCallback((routeMeta) => update({ routeMeta }), [update]);
  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    return CATALOG.filter(item => (query ? item.name.toLowerCase().includes(query) : item.category === inventoryCategory));
  }, [inventoryCategory, search]);

  const quantityFor = id => move.inventory.find(item => item.id === id)?.qty || 0;
  const changeQuantity = item => {
    const current = quantityFor(item.id);
    const next = current + 1;
    update({ inventory: [...move.inventory.filter(entry => entry.id !== item.id), { ...item, qty: next, quantity: next }] });
  };
  const decrement = id => update({ inventory: move.inventory.map(item => item.id === id ? { ...item, qty: item.qty - 1, quantity: item.qty - 1 } : item).filter(item => item.qty > 0) });
  const addCustomItem = () => {
    const name = customItemName.trim();
    if (!name) return;
    const id = `custom:${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
    changeQuantity({ id, name, category: 'Custom', room: 'custom', volume: 0, weight: 0, inventoryCompleteness: 'COMPLETE' });
    setCustomItemName('');
  };

  const continueFromRoute = () => {
    if (!move.route.origin || !move.route.destination) return setError('Add both locations to map your move.');
    setError('');
    setSurface('date');
  };
  const continueFromDate = () => {
    if (!move.moveDate) return setError('Choose a move date to continue.');
    setError('');
    setSurface('type');
  };
  const continueFromType = () => {
    if (!move.moveSize) return setError('Choose the move size that fits best.');
    setError('');
    setSurface('mode');
  };
  const chooseMode = quoteMode => {
    update({ quoteMode });
    setSurface(quoteMode === 'detailed' ? 'inventory' : 'access');
  };
  const updateAccess = (place, field, value) => update({ access: { ...move.access, [place]: { ...move.access[place], [field]: value } } });
  const findMovers = () => {
    const moveDetails = { origin: move.route.origin, destination: move.route.destination, moveDate: move.moveDate, moveWindow: move.moveWindow, moveSize: move.moveSize, distanceKm: move.routeMeta.distanceKm, durationMinutes: move.routeMeta.durationMinutes, access: move.access };
    sessionStorage.setItem('moveDetails', JSON.stringify(moveDetails));
    sessionStorage.setItem('inventory', JSON.stringify(move.inventory));
    sessionStorage.setItem('inventoryCompleteness', move.quoteMode === 'detailed' ? 'COMPLETE' : 'ESTIMATED');
    navigate('/results', { state: { moveDetails, inventory: move.inventory } });
  };

  const titles = {
    route: ['Where are you moving?', 'Start with the route. MOVD will keep it with you as the move takes shape.'],
    date: ['When are you moving?', 'Choose the day first. We will use it to show movers who can actually serve your move.'],
    type: ['What kind of move is this?', 'A rough move size gives the marketplace enough context to begin.'],
    mode: ['How much detail do you want to add?', 'Start quickly or build a more precise picture of what is moving.'],
    inventory: ['Build your move', 'Add only what helps. Every item becomes part of the move profile immediately.'],
    access: ['What should movers know about access?', 'Add only the access details that change the work. You can edit these later.'],
    review: ['Your move is taking shape', 'Review the essentials before MOVD finds options that fit.'],
  };
  const [title, subtitle] = titles[surface];

  const steps = useMemo(() => ([
    { key: 'route', label: 'Route' },
    { key: 'date', label: 'Date' },
    { key: 'type', label: 'Move size' },
    { key: 'mode', label: 'Detail level' },
    ...(move.quoteMode === 'detailed' ? [{ key: 'inventory', label: 'Inventory' }] : []),
    { key: 'access', label: 'Access' },
    { key: 'review', label: 'Review' },
  ]), [move.quoteMode]);

  return (
    <MovdJourneyShell onExit={onClose} steps={steps} currentStep={surface} aside={<MovdRouteMap origin={move.route.origin} destination={move.route.destination} onRouteChange={updateRouteMeta} />}>
      <div style={{ flexShrink: 0 }}>
        <p style={{ margin: 0, color: '#38BDF8', fontSize: '.66rem', fontWeight: 900, letterSpacing: '.16em' }}>BUILD / {surface.toUpperCase()}</p>
        <h1 style={{ fontSize: '2.3rem', lineHeight: .95, letterSpacing: '-.045em', fontWeight: 900, maxWidth: 650, margin: '6px 0 0' }}>{title}</h1>
        <p style={{ color: 'rgba(255,255,255,.64)', maxWidth: 490, fontSize: '.92rem', lineHeight: 1.4, margin: '6px 0 0' }}>{subtitle}</p>
      </div>

      <div style={{ marginTop: 14, flexShrink: 0 }}>
        <MoveProfile move={move} compact />
        {error && <p role="alert" style={{ color: movdTokens.color.danger, marginTop: 16, fontWeight: 700 }}>{error}</p>}

        {surface === 'route' && (
          <div style={{ marginTop: 24, display: 'grid', gap: 20 }}>
            <MovdFormRow columns={1} style={{ gap: 20 }}>
              <MovdFormSection label="From">
                <CanadianAddressAutocomplete
                  placeholder="Pickup location"
                  value={move.route.origin}
                  onChange={value => updateRoute('origin', typeof value === 'string' ? value : '')}
                  onSelect={value => updateRoute('origin', value.display_name)}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: movdTokens.color.paper, borderRadius: `${movdTokens.radius.control}px`, color: movdTokens.color.ink, '& fieldset': { borderColor: 'rgba(255,255,255,.7)' }, '&:hover fieldset': { borderColor: movdTokens.color.teal }, '&.Mui-focused fieldset': { borderColor: movdTokens.color.teal, borderWidth: 2 } }, '& .MuiOutlinedInput-input': { color: movdTokens.color.ink, fontWeight: 700, py: 1.7 } }}
                />
              </MovdFormSection>
              <MovdFormSection label="To">
                <CanadianAddressAutocomplete
                  placeholder="Drop-off location"
                  value={move.route.destination}
                  onChange={value => updateRoute('destination', typeof value === 'string' ? value : '')}
                  onSelect={value => updateRoute('destination', value.display_name)}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: movdTokens.color.paper, borderRadius: `${movdTokens.radius.control}px`, color: movdTokens.color.ink, '& fieldset': { borderColor: 'rgba(255,255,255,.7)' }, '&:hover fieldset': { borderColor: movdTokens.color.warm }, '&.Mui-focused fieldset': { borderColor: movdTokens.color.warm, borderWidth: 2 } }, '& .MuiOutlinedInput-input': { color: movdTokens.color.ink, fontWeight: 700, py: 1.7 } }}
                />
              </MovdFormSection>
            </MovdFormRow>
            <MovdFormActions primaryLabel="Continue with route" onPrimary={continueFromRoute} />
          </div>
        )}

        {surface === 'date' && (
          <div style={{ marginTop: 24, display: 'grid', gap: 20 }}>
            <MovdInput label="Move date" type="date" value={move.moveDate} onChange={event => update({ moveDate: event.target.value })} min={new Date().toISOString().split('T')[0]} style={{ maxWidth: 360 }} />
            <MovdFormSection label="Arrival window">
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {ARRIVAL_WINDOWS.map(window => (
                  <button key={window} type="button" onClick={() => update({ moveWindow: window })} style={pillStyle(move.moveWindow === window)}>{window}</button>
                ))}
              </div>
            </MovdFormSection>
            <MovdFormActions primaryLabel="Continue" onPrimary={continueFromDate} />
          </div>
        )}

        {surface === 'type' && (
          <div style={{ marginTop: 24 }}>
            <div style={{ display: 'grid', gap: 4, maxWidth: 500 }}>
              {MOVE_SIZES.map(size => (
                <button key={size.value} type="button" onClick={() => update({ moveSize: size.value })} style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', background: 'transparent', color: movdTokens.color.text, border: 'none', borderBottom: '1px solid rgba(255,255,255,.2)', borderRadius: 0, padding: '12px 0', fontFamily: 'inherit', fontSize: '1rem', fontWeight: 800, cursor: 'pointer' }}>
                  {size.label}
                  <span style={{ color: move.moveSize === size.value ? '#38BDF8' : 'transparent', fontWeight: 900 }}>✓</span>
                </button>
              ))}
            </div>
            <MovdFormActions primaryLabel="Continue" onPrimary={continueFromType} />
          </div>
        )}

        {surface === 'mode' && (
          <div style={{ marginTop: 24, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            <button type="button" onClick={() => chooseMode('quick')} style={{ textAlign: 'left', color: movdTokens.color.text, background: 'transparent', border: '1px solid rgba(255,255,255,.28)', borderRadius: movdTokens.radius.control, padding: 20, minHeight: 145, cursor: 'pointer', fontFamily: 'inherit' }}>
              <strong style={{ display: 'block', fontSize: '1rem' }}>Quick Quote</strong>
              <span style={{ display: 'block', color: 'rgba(255,255,255,.62)', marginTop: 8, fontSize: '.88rem' }}>I will provide the basics.</span>
            </button>
            <button type="button" onClick={() => chooseMode('detailed')} style={{ textAlign: 'left', color: movdTokens.color.text, background: 'transparent', border: '1px solid rgba(255,255,255,.28)', borderRadius: movdTokens.radius.control, padding: 20, minHeight: 145, cursor: 'pointer', fontFamily: 'inherit' }}>
              <strong style={{ display: 'block', fontSize: '1rem' }}>Detailed Move</strong>
              <span style={{ display: 'block', color: 'rgba(255,255,255,.62)', marginTop: 8, fontSize: '.88rem' }}>I want a more precise picture.</span>
            </button>
          </div>
        )}

        {surface === 'inventory' && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 8 }}>
              {ROOM_DEFS.map(room => (
                <button key={room.key} type="button" onClick={() => setInventoryCategory(room.name)} style={{ ...pillStyle(inventoryCategory === room.name), flexShrink: 0, borderRadius: 0 }}>{room.name}</button>
              ))}
            </div>
            <MovdInput label="Search inventory" value={search} onChange={event => setSearch(event.target.value)} placeholder="Search sofa, piano, boxes..." style={{ marginTop: 10 }} />
            <div style={{ marginTop: 12, maxHeight: 300, overflowY: 'auto', display: 'grid', gap: 4 }}>
              {filteredItems.map(item => (
                <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,.16)', padding: '8px 0' }}>
                  <span>{item.name}</span>
                  <MovdQuantityControl label={item.name} value={quantityFor(item.id)} onDecrease={() => decrement(item.id)} onIncrease={() => changeQuantity(item)} />
                </div>
              ))}
            </div>
            <MovdFormRow columns={2} style={{ marginTop: 10 }}>
              <MovdInput label="Custom item" value={customItemName} onChange={event => setCustomItemName(event.target.value)} placeholder="Add another item" />
              <MovdButton variant="secondary" onClick={addCustomItem} style={{ alignSelf: 'end' }}>Add custom</MovdButton>
            </MovdFormRow>
            <MovdFormActions primaryLabel="Continue with contents" onPrimary={() => setSurface('access')} style={{ marginTop: 16 }} />
          </div>
        )}

        {surface === 'access' && (
          <div style={{ marginTop: 24, display: 'grid', gap: 20, maxWidth: 560 }}>
            <MovdFormSection label="Pickup access">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
                <MovdInput label="Floor" value={move.access.origin.floor} onChange={event => updateAccess('origin', 'floor', event.target.value)} style={{ maxWidth: 130 }} />
                <button type="button" onClick={() => updateAccess('origin', 'elevator', !move.access.origin.elevator)} style={pillStyle(move.access.origin.elevator)}>Elevator</button>
                <button type="button" onClick={() => updateAccess('origin', 'stairs', !move.access.origin.stairs)} style={pillStyle(move.access.origin.stairs)}>Stairs</button>
              </div>
            </MovdFormSection>
            <MovdFormSection label="Drop-off access">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
                <MovdInput label="Floor" value={move.access.destination.floor} onChange={event => updateAccess('destination', 'floor', event.target.value)} style={{ maxWidth: 130 }} />
                <button type="button" onClick={() => updateAccess('destination', 'elevator', !move.access.destination.elevator)} style={pillStyle(move.access.destination.elevator)}>Elevator</button>
                <button type="button" onClick={() => updateAccess('destination', 'stairs', !move.access.destination.stairs)} style={pillStyle(move.access.destination.stairs)}>Stairs</button>
              </div>
            </MovdFormSection>
            <MovdFormActions primaryLabel="Review my move" onPrimary={() => setSurface('review')} />
          </div>
        )}

        {surface === 'review' && (
          <div style={{ marginTop: 24, display: 'grid', gap: 14, maxWidth: 560 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', color: '#7DD3FC' }}>
              <span aria-hidden="true">📦</span>
              <span>{move.quoteMode === 'quick' ? 'Quick quote selected. Inventory skipped.' : `${countItems(move.inventory)} items added.`}</span>
            </div>
            <p style={{ color: 'rgba(255,255,255,.7)', margin: 0 }}>{move.access.origin.elevator || move.access.origin.stairs || move.access.destination.elevator || move.access.destination.stairs ? 'Access details added to the move.' : 'No access details added yet.'}</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <button type="button" style={linkButtonStyle} onClick={() => setSurface('route')}>Edit route</button>
              <button type="button" style={linkButtonStyle} onClick={() => setSurface('date')}>Edit date</button>
              <button type="button" style={linkButtonStyle} onClick={() => setSurface(move.quoteMode === 'detailed' ? 'inventory' : 'mode')}>Edit move</button>
              <button type="button" style={linkButtonStyle} onClick={() => setSurface('access')}>Edit access</button>
            </div>
            <MovdFormActions primaryLabel="Find movers" onPrimary={findMovers} />
          </div>
        )}
      </div>
    </MovdJourneyShell>
  );
}
